import { useCallback } from "react";

import { openSearchPanel } from "@codemirror/search";
import { EditorView } from "@codemirror/view";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { Column } from "@tanstack/react-table";

import { DatasetRow } from "src/api/types";
import { FloatingWindow } from "src/base-components/FloatingWindow/FloatingWindow";
import { Pill } from "src/base-components/Pill";
import { PonEditor } from "src/base-components/PONEditor";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { CellWrapper } from "src/datasets/components/CellWrapper";
import { getErrorDetails } from "src/datasets/components/errorUtils";
import { JSONPill } from "src/datasets/components/JSONPill";
import { getIcon } from "src/datasets/DatasetTable/cells";
import {
  getNonProviderIntegrationNodeIcon,
  ponToJson,
  SUB_COLUMN_SEPARATOR,
} from "src/datasets/DatasetTable/utils";
import {
  ValidationResult,
  validateAny,
} from "src/datasets/DatasetTable/validators";
import { DatasetIntegrationNode } from "src/datasets/utils";
import { IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { copyTextToClipboard } from "src/utils/clipboard";
import { logger } from "src/utils/logger";
import { modifierKey } from "src/utils/platformModifierKey";

export const JSONEditorCell: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  isOpen: boolean;
  disabled: boolean;
  integrationNode?: DatasetIntegrationNode;
  type: string;
  warning: boolean;
  invalid: boolean;
  column: Column<DatasetRow>;
  rowIndex: number;
  error?: ValidationResult | undefined;
  postfix?: React.ReactNode;
  editing: boolean;
  selected: boolean;
}> = ({
  value,
  onChange,
  onClose,
  isOpen,
  disabled,
  integrationNode,
  type,
  warning,
  invalid,
  column,
  rowIndex,
  error,
  postfix,
  editing,
  selected,
}) => {
  const children = (
    <CellWrapper
      disabled={disabled}
      editing={editing}
      error={getErrorDetails(error, value, onChange)}
      invalid={invalid}
      postfix={postfix}
      selected={selected}
      warning={warning}
    >
      <JSONPill type={type} value={value} />
    </CellWrapper>
  );

  const onCopy = async () => {
    try {
      const json = ponToJson(value);
      const isInvalid = validateAny(json);

      if (!isInvalid) {
        await copyTextToClipboard(json);
        toastActions.success({
          title: "Copied JSON to clipboard",
        });
      } else {
        toastActions.failure({
          title: "Failed to copy JSON to clipboard",
          description: "Value is not valid JSON",
        });
      }
    } catch (e) {
      logger.error(e);
      toastActions.failure({
        title: "Failed to copy JSON to clipboard",
      });
    }
  };

  const handleOpenCodeSearch = useCallback(() => {
    const editorDom = document.querySelector<HTMLElement>(
      "[data-loc='floating-window'] .cm-editor",
    );
    if (editorDom) {
      const view = EditorView.findFromDOM(editorDom);
      if (view) {
        view.focus();
        openSearchPanel(view);
      }
    }
  }, []);

  if (disabled) {
    return children;
  }

  return (
    <FloatingWindow
      button={children}
      isOpen={isOpen}
      title={
        <>
          <div className="flex items-start gap-x-1.5">
            <Pill size="sm" variant="gray">
              <Pill.Text>#{rowIndex + 1}</Pill.Text>
            </Pill>
            <Pill size="sm" variant="gray">
              {integrationNode ? (
                <Pill.Icon
                  icon={getNonProviderIntegrationNodeIcon(
                    integrationNode.provider,
                  )}
                />
              ) : (
                <Pill.Icon
                  icon={
                    column.columnDef.meta?.archetype?.customIcon ??
                    getIcon(
                      column.columnDef.meta?.archetype?.desiredType ?? "any",
                    )
                  }
                />
              )}
              <Pill.Text>
                {renderColumnName(column.columnDef.meta?.archetype?.name ?? "")}
              </Pill.Text>
            </Pill>
          </div>
        </>
      }
      titleRight={
        <Tooltip
          placement="bottom"
          title={
            <>
              <span className="mr-2">Code search</span>
              <Pill size="sm" variant="dark-gray">
                <Pill.Text fontType="text">{modifierKey} SHIFT F</Pill.Text>
              </Pill>
            </>
          }
          asChild
        >
          <IconButton
            aria-label="Code search"
            dataLoc="open-code-search"
            icon={faMagnifyingGlass}
            size="xs"
            onClick={handleOpenCodeSearch}
          />
        </Tooltip>
      }
      onClose={onClose}
      onCopy={onCopy}
    >
      <div className="flex h-full flex-1 flex-col gap-y-1.5">
        {integrationNode?.provider === NODE_TYPE.LOOP_NODE && (
          <p className="font-inter-normal-12px text-gray-500">
            For each iteration in the loop, mock response below will be used.
          </p>
        )}
        <PonEditor value={value} onChange={onChange} />
      </div>
    </FloatingWindow>
  );
};

const renderColumnName = (name: string) => {
  if (name.includes(SUB_COLUMN_SEPARATOR)) {
    return name.split(SUB_COLUMN_SEPARATOR)[1];
  }

  return name;
};
